/**
 * Supabase Client (Shared)
 *
 * Platform-agnostic Supabase client configuration.
 * Uses adapters for environment variables and storage.
 *
 * IMPORTANT: Platform-specific initialization must happen before using this module.
 * Call initializeSupabase() with platform-specific options after setting up adapters.
 */

import { createClient } from '@supabase/supabase-js';
import { getEnv, getRequiredEnv } from '../adapters/env';
import { getStorageAdapter } from '../adapters/storage';
import { isWeb, isMobile } from '../adapters/platform';
import apiKeyManager from './apiKeyManager';
import { permissionCache } from './permissionCache';

// Supabase client instance (lazy initialized)
let supabase = null;

// Local company ID cache
let localCompanyIdCache = {
  value: null,
  userId: null,
  timestamp: null,
};

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Initialize Supabase client
 *
 * @param {Object} options - Platform-specific options
 * @param {Object} options.storage - Storage implementation (localStorage for web, AsyncStorage wrapper for mobile)
 * @param {string} options.redirectUrl - Auth redirect URL (optional, web only)
 */
export function initializeSupabase(options = {}) {
  if (supabase) {
    console.log('[Supabase] Already initialized');
    return supabase;
  }

  const supabaseUrl = getRequiredEnv('SUPABASE_URL');
  const supabaseAnonKey = apiKeyManager.getSupabaseAnonKey();

  // Get storage from options or adapter
  const authStorage = options.storage || getStorageAdapter();

  // Build auth config
  const authConfig = {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: isWeb(), // Only detect URL on web
    storage: authStorage,
    flowType: 'pkce',
    // ponytail: no-op lock. The default navigatorLock serializes auth ops
    // (getSession/getUser/refresh) behind navigator.locks, and the
    // PostgREST/Storage/Functions clients also wait on it to attach the JWT
    // to every outbound request. When the browser suspends the auto-refresh
    // timer mid-lock during a long tab-hidden interval, the lock stays held
    // and *every* subsequent supabase call hangs until reload — that's the
    // "actions don't work after tab-out" symptom (commits 3392704, bf3a7d9,
    // 8536a11 were all narrower workarounds for the same root cause).
    // Trade-off: multi-tab auto-refresh may race (each tab refreshes its own
    // session), which is redundant but harmless.
    lock: (_name, _timeout, fn) => fn(),
  };

  // Add redirect URL if provided (web only)
  if (options.redirectUrl) {
    authConfig.redirectTo = options.redirectUrl;
  }

  // ponytail: 15s per-request fetch timeout via AbortController.
  // THIS is the real tab-out hang. supabase-js calls window.fetch with no
  // timeout. Chrome can leave an HTTP/2 stream suspended through a tab-hide
  // and never resume it; new requests multiplexed onto the same connection
  // queue behind the dead stream. Until reload. API logs confirm the symptom:
  // `_createJob` reaches "firing insert at 0 ms" and no POST /rest/v1/jobs
  // ever arrives server-side. Prior fixes (no-op lock, _refreshAccessToken
  // timeout) couldn't help — the fetch itself never resolves.
  //
  // Wrapping global.fetch with an AbortController kills any request stuck
  // >15s. The abort frees the underlying stream so subsequent requests get a
  // fresh connection. Caller sees a normal fetch error and can retry.
  //
  // Preserves the caller's own AbortSignal (Storage uses it for uploads).
  const FETCH_TIMEOUT_MS = 15000;
  const timeoutFetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn('[Supabase] fetch timeout >15s, aborting:', url);
      controller.abort(new Error('fetch timeout after 15s'));
    }, FETCH_TIMEOUT_MS);

    // Chain caller's signal -> our controller so external aborts still work.
    if (init.signal) {
      if (init.signal.aborted) {
        controller.abort(init.signal.reason);
      } else {
        init.signal.addEventListener('abort', () => controller.abort(init.signal.reason), { once: true });
      }
    }

    return fetch(input, { ...init, signal: controller.signal }).finally(() => {
      clearTimeout(timeoutId);
    });
  };

  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: authConfig,
    db: {
      schema: 'public',
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-js/2.x',
        'x-connection-pool': 'transaction',
      },
      fetch: timeoutFetch,
    },
  });

  // ponytail: override the accessToken getter so REST/Realtime don't hang
  // behind auth-js's internal pendingInLock queue. auth-js's _acquireLock
  // serializes via pendingInLock REGARDLESS of the lock option. When a
  // refresh fetch is suspended through a tab-hide, the lock callback never
  // returns, lockAcquired stays true, and every subsequent getSession()
  // queues forever. Setting supabase.accessToken short-circuits the
  // _getAccessToken path entirely (it checks `this.accessToken` first), so
  // REST requests can fetch the JWT straight from storage and never queue.
  //
  // Auto-refresh ticker still keeps the storage token fresh on its own
  // schedule. On rare 401 from an expired token, the caller errors and the
  // user retries — far better than an indefinite hang.
  const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
  // ponytail: must await — mobile SecureStore.getItem returns a Promise, web localStorage returns a string.
  const readTokenFromStorage = async () => {
    try {
      const raw = await authStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed?.access_token ?? null;
    } catch {
      return null;
    }
  };
  supabase.accessToken = async () => {
    const t0 = Date.now();
    try {
      const result = await Promise.race([
        supabase.auth.getSession(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getSession >2s')), 2000)
        ),
      ]);
      const token = result?.data?.session?.access_token;
      if (token) return token;
      throw new Error('getSession returned no token');
    } catch (err) {
      const fallback = await readTokenFromStorage();
      console.warn(
        `[Supabase] accessToken fast-path fallback after ${Date.now() - t0}ms:`,
        err?.message,
        fallback ? '(using storage token)' : '(no storage token, using anon key)'
      );
      return fallback ?? supabaseAnonKey;
    }
  };

  // ponytail: monkey-patch _refreshAccessToken with a hard 10s timeout.
  // auth-js v2.95.3 GoTrueClient.js:1971 _callRefreshToken stores an
  // in-flight Deferred in `this.refreshingDeferred` and clears it in the
  // finally — but the await on _refreshAccessToken has no timeout. If the
  // browser holds a fetch suspended through a tab-hide and never resumes it,
  // the try/finally never completes, refreshingDeferred stays set, and every
  // future getSession() call (every REST request, getUser, etc.) awaits the
  // same dead promise forever. Until reload. That's the "Schedule hangs at
  // firing insert: 0 ms with no network activity" symptom that survived
  // 3392704, bf3a7d9, 8536a11, and 1cfb267.
  //
  // Wrap _refreshAccessToken in a race so a stuck fetch rejects after 10s.
  // The catch in _callRefreshToken then rejects the deferred and the finally
  // clears it, leaving the next caller free to start a fresh refresh.
  const authClient = supabase.auth;
  const originalRefresh = authClient._refreshAccessToken?.bind(authClient);
  if (originalRefresh) {
    authClient._refreshAccessToken = (refreshToken) =>
      Promise.race([
        originalRefresh(refreshToken),
        new Promise((_, reject) =>
          setTimeout(() => {
            if (authClient.refreshingDeferred) {
              console.warn('[Supabase] _refreshAccessToken stuck >10s; clearing refreshingDeferred');
              authClient.refreshingDeferred = null;
            }
            reject(new Error('_refreshAccessToken timeout after 10s'));
          }, 10000)
        ),
      ]);
  }

  // Set up auth state change handler
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'TOKEN_REFRESHED' && session?.access_token) {
      console.log('[Supabase] Token refreshed, updating realtime auth');
      supabase.realtime.setAuth(session.access_token);
    } else if (event === 'SIGNED_OUT') {
      console.log('[Supabase] User signed out');
      clearClientCompanyIdCache();
    }
  });

  // Build marker so we can tell from a console log whether the deployed
  // bundle has the no-op lock + refresh timeout + fetch timeout patches.
  // Bump this string whenever this file changes in a load-bearing way.
  console.log('[Supabase] Client initialized');
  return supabase;
}

/**
 * Get Supabase client (must be initialized first)
 */
export function getSupabase() {
  if (!supabase) {
    throw new Error(
      'Supabase not initialized. Call initializeSupabase() first.'
    );
  }
  return supabase;
}

/**
 * Check if Supabase is initialized
 */
export function isSupabaseInitialized() {
  return supabase !== null;
}

// ============================================
// Auth Helper Functions
// ============================================

export async function getCurrentUser() {
  const client = getSupabase();
  const {
    data: { user },
    error,
  } = await client.auth.getUser();
  if (error) throw error;
  return user;
}

export async function getCurrentSession() {
  const client = getSupabase();
  const {
    data: { session },
    error,
  } = await client.auth.getSession();
  if (error) throw error;
  return session;
}

export async function signOut() {
  const client = getSupabase();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

// ============================================
// Company ID Functions
// ============================================

/**
 * Get company ID for a user
 */
export async function getCompanyId(user) {
  if (!user?.id) {
    return null;
  }

  const client = getSupabase();

  // 1. FASTEST: Check user metadata (already in memory)
  const metadataCompanyId = user.user_metadata?.company_id;
  if (metadataCompanyId) {
    localCompanyIdCache.value = metadataCompanyId;
    localCompanyIdCache.userId = user.id;
    localCompanyIdCache.timestamp = Date.now();
    return metadataCompanyId;
  }

  // 2. FAST: Check local cache
  const now = Date.now();
  if (
    localCompanyIdCache.value &&
    localCompanyIdCache.userId === user.id &&
    localCompanyIdCache.timestamp &&
    now - localCompanyIdCache.timestamp < CACHE_DURATION
  ) {
    return localCompanyIdCache.value;
  }

  // 3. FAST: Check permission cache
  if (permissionCache.isCacheValid()) {
    const cachedPermissions = permissionCache.getCachedPermissions();
    if (cachedPermissions?.userId === user.id && cachedPermissions?.companyId) {
      localCompanyIdCache.value = cachedPermissions.companyId;
      localCompanyIdCache.userId = user.id;
      localCompanyIdCache.timestamp = now;
      return cachedPermissions.companyId;
    }
  }

  // 4. SLOW: Query database (only happens once per session)
  console.log('[getCompanyId] Cache miss, querying database...');

  const withTimeout = (promise, ms = 10000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Database query timeout')), ms)
      ),
    ]);
  };

  try {
    // Try user_access_cache first
    const { data: accessData } = await withTimeout(
      client
        .from('user_access_cache')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle()
    );

    if (accessData?.company_id) {
      localCompanyIdCache.value = accessData.company_id;
      localCompanyIdCache.userId = user.id;
      localCompanyIdCache.timestamp = Date.now();
      return accessData.company_id;
    }

    // Fallback: companies table for owners
    const { data: companyData } = await withTimeout(
      client
        .from('companies')
        .select('id')
        .eq('owner_user_id', user.id)
        .maybeSingle()
    );

    if (companyData?.id) {
      localCompanyIdCache.value = companyData.id;
      localCompanyIdCache.userId = user.id;
      localCompanyIdCache.timestamp = Date.now();
      return companyData.id;
    }

    return null;
  } catch (error) {
    console.error(
      '[getCompanyId] Database query failed or timed out:',
      error.message
    );
    return null;
  }
}

/**
 * Clear the local company ID cache
 */
export function clearClientCompanyIdCache() {
  localCompanyIdCache.value = null;
  localCompanyIdCache.userId = null;
  localCompanyIdCache.timestamp = null;
  console.log('[supabase] Local company ID cache cleared');
}

// ============================================
// Permission Functions
// ============================================

/**
 * Check if user has a specific permission
 */
export async function hasPermission(permission) {
  try {
    const client = getSupabase();
    const { data: hasAccess, error } = await client.rpc('has_permission', {
      p_permission: permission,
    });

    if (error) {
      console.error('[hasPermission] Error checking permission:', error);
      if (
        error.code === '42883' ||
        error.message.includes('function') ||
        error.message.includes('does not exist')
      ) {
        console.warn(
          '[hasPermission] Function not found, using fallback (allowing access)'
        );
        return true;
      }
      return false;
    }

    return hasAccess || false;
  } catch (error) {
    console.error('[hasPermission] Error during permission check:', error);
    return true;
  }
}

// ============================================
// Database Helper Functions
// ============================================

/**
 * Create a company for the authenticated user. Reads owner details from user_metadata,
 * which the platform-specific onboarding flows populate before calling this.
 *
 * The Apple-relay leak this guards against: we use meta.company_email (the user-entered
 * public email collected during onboarding) rather than user.email, which on Apple
 * Sign-In is a privaterelay.appleid.com address that would otherwise land on invoices.
 * When no company email was collected, a non-relay account email is used instead.
 */
export async function createCompanyWithOwner(user) {
  try {
    const meta = user?.user_metadata || {};
    const companyName = meta.company_name || 'My Company';
    const ownerFirstName = meta.first_name || null;
    const ownerLastName = meta.last_name || null;
    const ownerPhone = meta.phone || null;
    // Without a collected company email (plain email/password signup), the
    // account email is the contact address — unless it is an Apple relay (#464).
    const accountEmail = user?.email && !/@privaterelay\.appleid\.com$/i.test(user.email) ? user.email : null;
    const ownerEmail = meta.company_email || accountEmail;
    const signupSource = meta.signup_source || 'website';

    const client = getSupabase();
    const { data: companyId, error } = await client.rpc(
      'create_company_with_owner',
      {
        p_company_name: companyName,
        p_owner_first_name: ownerFirstName,
        p_owner_last_name: ownerLastName,
        p_owner_phone: ownerPhone,
        p_owner_email: ownerEmail,
        p_signup_source: signupSource,
      }
    );

    if (error) {
      console.error('[createCompanyWithOwner] Error creating company:', error);
      throw error;
    }

    console.log('[createCompanyWithOwner] Company created successfully:', companyId);
    return companyId;
  } catch (error) {
    console.error('[createCompanyWithOwner] Error during company creation:', error);
    throw error;
  }
}

/**
 * Add employee to company
 */
export async function addEmployee(
  userId,
  firstName,
  lastName,
  email,
  phone = null,
  role = null,
  permissions = []
) {
  try {
    const client = getSupabase();
    const { data: employeeId, error } = await client.rpc('add_employee', {
      p_user_id: userId,
      p_first_name: firstName,
      p_last_name: lastName,
      p_email: email,
      p_phone: phone,
      p_role: role,
      p_permissions: permissions,
    });

    if (error) {
      console.error('[addEmployee] Error adding employee:', error);
      throw error;
    }

    console.log('[addEmployee] Employee added successfully:', employeeId);
    return employeeId;
  } catch (error) {
    console.error('[addEmployee] Error during employee creation:', error);
    throw error;
  }
}

/**
 * Batch update job status
 */
export async function batchUpdateJobStatus(jobIds, newStatus) {
  try {
    const client = getSupabase();
    const { data: updatedCount, error } = await client.rpc(
      'batch_update_job_status',
      {
        p_job_ids: jobIds,
        p_new_status: newStatus,
      }
    );

    if (error) {
      console.error('[batchUpdateJobStatus] Error updating job status:', error);
      throw error;
    }

    console.log('[batchUpdateJobStatus] Updated jobs count:', updatedCount);
    return updatedCount;
  } catch (error) {
    console.error('[batchUpdateJobStatus] Error during batch update:', error);
    throw error;
  }
}

/**
 * Invalidate cache
 */
export async function invalidateCache(keyPattern) {
  try {
    const client = getSupabase();
    const { error } = await client.rpc('invalidate_cache', {
      p_key_pattern: keyPattern,
    });

    if (error) {
      console.error('[invalidateCache] Error invalidating cache:', error);
      throw error;
    }

    console.log('[invalidateCache] Cache invalidated for pattern:', keyPattern);
    return true;
  } catch (error) {
    console.error('[invalidateCache] Error during cache invalidation:', error);
    throw error;
  }
}

/**
 * Health check
 */
export async function healthCheck() {
  try {
    const client = getSupabase();
    const { data: healthData, error } = await client.rpc('health_check');

    if (error) {
      console.error('[healthCheck] Error getting health status:', error);
      throw error;
    }

    console.log('[healthCheck] Health check completed:', healthData);
    return healthData;
  } catch (error) {
    console.error('[healthCheck] Error during health check:', error);
    throw error;
  }
}

// ============================================
// Real-time Subscriptions
// ============================================

/**
 * Create a real-time subscription
 */
export function createRealtimeSubscription(table, event, callback, filter = {}) {
  const client = getSupabase();
  return client
    .channel(`${table}_${event}`)
    .on(
      'postgres_changes',
      {
        event,
        schema: 'public',
        table,
        filter,
      },
      callback
    )
    .subscribe();
}

// ============================================
// Query Helpers
// ============================================

/**
 * Company-based data filtering helper
 */
export function withCompanyFilter(query, companyId) {
  if (!companyId) {
    throw new Error('Company ID is required for data operations');
  }
  return query.eq('company_id', companyId);
}

/**
 * Batch operation helper
 */
export async function batchOperation(operations, batchSize = 100) {
  const results = [];

  for (let i = 0; i < operations.length; i += batchSize) {
    const batch = operations.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch);
    results.push(...batchResults);
  }

  return results;
}

/**
 * Handle Supabase errors
 */
export function handleSupabaseError(error, operation = 'operation') {
  console.error(`Supabase ${operation} error:`, error);

  if (error.code === 'PGRST116') {
    throw new Error('Access denied. Insufficient permissions.');
  }

  if (error.code === 'PGRST301') {
    throw new Error('Authentication required. Please log in again.');
  }

  if (error.code === 'PGRST302') {
    throw new Error('Row Level Security policy violation.');
  }

  throw new Error(error.message || `Failed to complete ${operation}`);
}

// Default export for convenience
export { supabase };
export default {
  initializeSupabase,
  getSupabase,
  isSupabaseInitialized,
  getCurrentUser,
  getCurrentSession,
  signOut,
  getCompanyId,
  clearClientCompanyIdCache,
  hasPermission,
  createCompanyWithOwner,
  addEmployee,
  batchUpdateJobStatus,
  invalidateCache,
  healthCheck,
  createRealtimeSubscription,
  withCompanyFilter,
  batchOperation,
  handleSupabaseError,
};
